/**
 * AttributeFieldChip — pill-чип-обёртка над тем же inline-редактором,
 * что используется в rail (`RailAttributeField`) и в ячейках таблиц.
 *
 * Используется в `CreateProjectDialog` / `CreateTaskDialog` в горизонтальном
 * ряду под названием. По дизайну совпадает с Linear: иконка + label /
 * placeholder, при клике сразу открывается родной поповер нужного контрола
 * (список опций / календарь / поиск персоны / input / switch).
 *
 * Чтобы контролы рендерились в pill-стиле, в них проброшен `triggerVariant="chip"`.
 * Никакого собственного `Popover` / `PopoverContent` тут нет — этим
 * управляет сам контрол. Это значит, что для каждого типа атрибута ровно
 * один поповер (а не вложенные), и стиль 1-в-1 совпадает с rail.
 */
import type { ReactNode } from "react"
import type { IconSvgElement } from "@hugeicons/react"
import type { AttributeDefinition } from "@/api/metadata/useAttributeDefinitions"
import type { SchemeStatus } from "@/api/statusSchemes/useStatusScheme"
import { CellSelect, type CellSelectOption } from "@/components/cell-controls/cell-controls"
import { CellTextField } from "@/components/cell-controls/cell-controls"
import { DatePicker } from "@/components/ui/date-range-picker"
import { PersonCombobox } from "@/components/ui/person-combobox"
import { Switch } from "@/components/ui/switch"
import { PeopleMultiSelect } from "@/features/attributes/people-multi-select"
import { getAttributeIcon } from "@/features/attributes/attribute-presentation"
import { resolveColor } from "@/features/attributes/attribute-table-columns"
import { parseLocalDate, formatLocalDate } from "@/lib/date"
import { HugeiconsIcon } from "@hugeicons/react"

type ChipProps = {
  definition: AttributeDefinition
  value: unknown
  onCommit: (next: unknown) => void
  statusOptions?: SchemeStatus[]
  slugOptions?: Record<string, CellSelectOption[]>
  placeholder?: string
}

function isPersonReference(def: AttributeDefinition) {
  return (
    def.attribute_type === "user" ||
    (def.attribute_type === "reference" && def.config.target_object_type === "person")
  )
}

function isPersonReferenceList(def: AttributeDefinition) {
  return (
    def.attribute_type === "reference_list" && def.config.target_object_type === "person"
  )
}

function getChipIcon(definition: AttributeDefinition): IconSvgElement | null {
  return (getAttributeIcon(definition.attribute_type, definition.config?.target_object_type) as IconSvgElement) ?? null
}

function LeadingIcon({ icon }: { icon: IconSvgElement | null }) {
  if (!icon) return null
  return (
    <HugeiconsIcon
      icon={icon}
      strokeWidth={1.8}
      className="size-3.5 shrink-0 text-muted-foreground"
    />
  )
}

function asCellSelectOptions(
  options: { value: string; label: string; color?: string }[],
): CellSelectOption[] {
  return options.map((opt) => ({
    value: opt.value,
    label: opt.label,
    color: opt.color ? resolveColor(opt.color) : undefined,
  }))
}

export function AttributeFieldChip({
  definition,
  value,
  onCommit,
  statusOptions,
  slugOptions,
  placeholder,
}: ChipProps) {
  const icon = getChipIcon(definition)
  const leadingIcon = <LeadingIcon icon={icon} />

  // System status — список из status scheme
  if (definition.slug === "status" && statusOptions && statusOptions.length > 0) {
    const options = statusOptions.map((s) => ({
      value: s.slug,
      label: s.label,
      color: s.color,
    }))
    return (
      <CellSelect
        value={typeof value === "string" ? value : null}
        options={asCellSelectOptions(options)}
        onChange={(next) => onCommit(next)}
        clearable={false}
        itemVariant="plain"
        triggerVariant="chip"
        leadingIcon={leadingIcon}
        placeholder={placeholder ?? definition.name}
        className="max-w-[180px]"
      />
    )
  }

  // Slug override (state, assignee_id, priority, и т.п.)
  if (slugOptions?.[definition.slug]) {
    return (
      <CellSelect
        value={typeof value === "string" ? value : null}
        options={slugOptions[definition.slug]}
        onChange={(next) => onCommit(next)}
        clearable={!["status", "state", "priority"].includes(definition.slug)}
        itemVariant="plain"
        triggerVariant="chip"
        leadingIcon={leadingIcon}
        placeholder={placeholder ?? definition.name}
        className="max-w-[180px]"
      />
    )
  }

  // Reference to person
  if (isPersonReference(definition)) {
    return (
      <PersonCombobox
        value={typeof value === "string" ? value : null}
        onChange={(personId) => onCommit(personId || null)}
        allowClear
        triggerVariant="chip"
        leadingIcon={leadingIcon}
        placeholder={placeholder ?? definition.name}
      />
    )
  }

  // Reference list of persons
  if (isPersonReferenceList(definition)) {
    return (
      <PeopleMultiSelect
        value={Array.isArray(value) ? (value as string[]) : null}
        onCommit={(next) => onCommit(next)}
        triggerVariant="chip"
        leadingIcon={leadingIcon}
        placeholder={placeholder ?? definition.name}
      />
    )
  }

  // Select / generic reference
  if (
    definition.attribute_type === "select" ||
    (definition.attribute_type === "reference" && (definition.config.options?.length ?? 0) > 0)
  ) {
    const options = definition.config.options ?? []
    return (
      <CellSelect
        value={typeof value === "string" ? value : null}
        options={asCellSelectOptions(options)}
        onChange={(next) => onCommit(next)}
        clearable
        itemVariant="plain"
        triggerVariant="chip"
        leadingIcon={leadingIcon}
        placeholder={placeholder ?? definition.name}
        className="max-w-[180px]"
      />
    )
  }

  // Date
  if (definition.attribute_type === "date") {
    const dateValue =
      typeof value === "string" && value && !Number.isNaN(Date.parse(value))
        ? parseLocalDate(value)
        : undefined
    return (
      <DatePicker
        mode="single"
        value={dateValue}
        onChange={(date) => onCommit(date ? formatLocalDate(date) : null)}
        placeholder={placeholder ?? definition.name}
        triggerVariant="chip"
        leadingIcon={leadingIcon}
      />
    )
  }

  // Datetime
  if (definition.attribute_type === "datetime") {
    return (
      <CellTextField
        type="datetime-local"
        value={typeof value === "string" ? value : null}
        onCommit={(next) => onCommit(next)}
        triggerVariant="chip"
        leadingIcon={leadingIcon}
        placeholder={placeholder ?? definition.name}
      />
    )
  }

  // Number
  if (definition.attribute_type === "number") {
    return (
      <CellTextField
        type="number"
        value={
          typeof value === "number"
            ? String(value)
            : typeof value === "string" && value !== ""
              ? value
              : null
        }
        onCommit={(next) => {
          if (next === null) {
            onCommit(null)
            return
          }
          const parsed = Number(next)
          onCommit(Number.isNaN(parsed) ? null : parsed)
        }}
        triggerVariant="chip"
        leadingIcon={leadingIcon}
        placeholder={placeholder ?? definition.name}
      />
    )
  }

  // Checkbox
  if (definition.attribute_type === "checkbox") {
    // Чекбокс не нуждается в popover-обёртке. Оборачиваем Switch в pill-чип.
    return (
      <ChipWrapper>
        {leadingIcon}
        <Switch
          checked={value === true}
          onCheckedChange={(nextValue) => onCommit(nextValue)}
        />
      </ChipWrapper>
    )
  }

  // Default: text-like (text / email / phone / url / long_text / …)
  return (
    <CellTextField
      value={typeof value === "string" || typeof value === "number" ? String(value) : null}
      onCommit={(next) => onCommit(next)}
      triggerVariant="chip"
      leadingIcon={leadingIcon}
      placeholder={placeholder ?? definition.name}
    />
  )
}

function ChipWrapper({ children }: { children: ReactNode }) {
  return (
    <div className="group/chip inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border border-border/80 bg-background px-2.5 text-xs leading-none font-normal text-foreground/80 transition-colors">
      {children}
    </div>
  )
}
